/**
 * IB Pathway Manager — admin CRUD for the 3 IB Pathway collections.
 * Tabs: Faculties · Streams · Universities
 * Schema-driven (same pattern as before). Uses existing CMS auth.
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useCMSAuth } from '@/context/CMSAuthContext';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import PublishStatusCard from '@/components/admin/PublishStatusCard';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '@/components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Plus, Pencil, Trash2, RefreshCw, Loader2, Search, ExternalLink, GraduationCap, Globe2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const SCHEMAS = {
  faculties: {
    label: 'Faculties', singular: 'Faculty',
    cols: ['slug', 'label', 'icon', 'color', 'display_order'],
    fields: [
      { key: 'slug', label: 'Slug', type: 'text', required: true, hint: 'e.g. psychology, datascience' },
      { key: 'label', label: 'Display name', type: 'text', required: true },
      { key: 'icon', label: 'Emoji icon', type: 'text', hint: 'e.g. 🧠' },
      { key: 'color', label: 'Color', type: 'text', hint: 'purple / coral / blue / amber / teal / green / pink' },
      { key: 'ibNote', label: 'IB lens note (200-400 chars)', type: 'textarea' },
      { key: 'ibCombos', label: 'IB combos (one per line)', type: 'lines' },
      { key: 'display_order', label: 'Display order', type: 'number' },
      { key: 'is_active', label: 'Active', type: 'bool', default: true },
    ],
  },
  streams: {
    label: 'Streams', singular: 'Stream',
    cols: ['faculty_slug', 'sid', 'title', 'sub', 'display_order'],
    fields: [
      { key: 'faculty_slug', label: 'Faculty slug', type: 'select', source: 'faculties', required: true },
      { key: 'sid', label: 'Stream id (slug)', type: 'text', required: true },
      { key: 'icon', label: 'Emoji icon', type: 'text' },
      { key: 'title', label: 'Title', type: 'text', required: true },
      { key: 'sub', label: 'Subtitle / short hook', type: 'text' },
      { key: 'fitColor', label: 'Fit chip color', type: 'text', hint: 'purple / coral / blue / amber / teal / green / pink' },
      { key: 'ibFit', label: 'IB fit (full description)', type: 'textarea' },
      { key: 'programs', label: 'Programmes (JSON array of {name, note, countries})', type: 'json',
        hint: '[{"name":"BSc Psychology","note":"...","countries":["AU","UK","CA"]}]' },
      { key: 'careers', label: 'Careers (one per line)', type: 'lines' },
      { key: 'careersNote', label: 'Careers note (India return etc.)', type: 'textarea' },
      { key: 'display_order', label: 'Display order', type: 'number' },
      { key: 'is_active', label: 'Active', type: 'bool', default: true },
    ],
  },
  universities: {
    label: 'Universities', singular: 'University',
    cols: ['faculty_slug', 'sid', 'program_index', 'name', 'country', 'rank'],
    fields: [
      { key: 'faculty_slug', label: 'Faculty slug', type: 'select', source: 'faculties', required: true },
      { key: 'sid', label: 'Stream sid', type: 'select', source: 'streams', sourceKey: 'sid', required: true },
      { key: 'program_index', label: 'Programme index (0-based)', type: 'number', required: true },
      { key: 'name', label: 'University name', type: 'text', required: true },
      { key: 'country', label: 'Country code (AU/UK/CA/NZ/US)', type: 'text', required: true },
      { key: 'degree', label: 'Degree title', type: 'text' },
      { key: 'rank', label: 'Rank (e.g. #14)', type: 'text' },
      { key: 'rankLabel', label: 'Rank source', type: 'text', hint: 'QS 2026 / THE 2025 etc.' },
      { key: 'fee', label: 'Fee', type: 'text', hint: 'e.g. AUD 53,600' },
      { key: 'feeUnit', label: 'Fee unit', type: 'text', hint: 'e.g. /year for international' },
      { key: 'duration', label: 'Duration', type: 'text' },
      { key: 'ib', label: 'IB requirement', type: 'text', hint: 'e.g. 36-40' },
      { key: 'accred', label: 'Accreditation', type: 'text' },
      { key: 'brief', label: 'Brief description', type: 'textarea' },
      { key: 'display_order', label: 'Display order', type: 'number' },
      { key: 'is_active', label: 'Active', type: 'bool', default: true },
    ],
  },
};

const RESOURCES = Object.keys(SCHEMAS);

const emptyForm = (schema) => {
  const o = {};
  schema.fields.forEach(f => {
    if (f.type === 'bool') o[f.key] = f.default ?? false;
    else if (f.type === 'lines' || f.type === 'json') o[f.key] = '';
    else o[f.key] = '';
  });
  return o;
};

const toForm = (schema, record) => {
  const f = {};
  schema.fields.forEach(fld => {
    const v = record?.[fld.key];
    if (fld.type === 'lines') f[fld.key] = Array.isArray(v) ? v.join('\n') : (v || '');
    else if (fld.type === 'json') f[fld.key] = v ? JSON.stringify(v, null, 2) : '';
    else if (fld.type === 'bool') f[fld.key] = !!v;
    else f[fld.key] = v ?? '';
  });
  return f;
};

const fromForm = (schema, form) => {
  const p = {};
  schema.fields.forEach(fld => {
    let v = form[fld.key];
    if (v === '' || v == null) {
      if (fld.type === 'bool') p[fld.key] = false;
      return;
    }
    if (fld.type === 'number') p[fld.key] = Number(v);
    else if (fld.type === 'bool') p[fld.key] = !!v;
    else if (fld.type === 'lines') p[fld.key] = String(v).split('\n').map(s => s.trim()).filter(Boolean);
    else if (fld.type === 'json') {
      try { p[fld.key] = JSON.parse(v); } catch { throw new Error(`Invalid JSON in ${fld.label}`); }
    } else p[fld.key] = v;
  });
  return p;
};

const FieldInput = ({ field, value, onChange, refs }) => {
  if (field.type === 'textarea') return <Textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={3} />;
  if (field.type === 'lines') return <Textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={4} className="font-mono text-xs" placeholder={field.hint} />;
  if (field.type === 'json') return <Textarea value={value || ''} onChange={e => onChange(e.target.value)} rows={6} className="font-mono text-xs" placeholder={field.hint} />;
  if (field.type === 'bool') return <Switch checked={!!value} onCheckedChange={onChange} />;
  if (field.type === 'select') {
    const opts = refs?.[field.source] || [];
    const labelKey = field.sourceKey || 'slug';
    const seen = new Set();
    return (
      <select value={value || ''} onChange={e => onChange(e.target.value)}
        className="w-full h-10 px-3 rounded-md border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm">
        <option value="">— select —</option>
        {opts.filter(o => { const k = o[labelKey]; if (seen.has(k)) return false; seen.add(k); return true; }).map(o => (
          <option key={o[labelKey]} value={o[labelKey]}>
            {o.label || o.title || o[labelKey]} ({o[labelKey]})
          </option>
        ))}
      </select>
    );
  }
  return <Input type={field.type === 'number' ? 'number' : 'text'} value={value ?? ''} onChange={e => onChange(e.target.value)} placeholder={field.hint} />;
};

const ResourceTab = ({ resource, token, refs, refetchRefs }) => {
  const schema = SCHEMAS[resource];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const headers = useMemo(() => ({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }), [token]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/ib/admin/${resource}`, { headers });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setItems(await r.json());
    } catch (e) { toast.error(`Load ${schema.label} failed: ${e.message}`); }
    finally { setLoading(false); }
  }, [resource, headers, schema.label]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    for (const f of schema.fields) {
      if (f.required && (form[f.key] === '' || form[f.key] == null)) {
        toast.error(`${f.label} is required`); return;
      }
    }
    let payload;
    try { payload = fromForm(schema, form); } catch (e) { toast.error(e.message); return; }
    setSaving(true);
    try {
      const isEdit = editing && editing.id;
      const url = isEdit ? `${API}/api/ib/admin/${resource}/${editing.id}` : `${API}/api/ib/admin/${resource}`;
      const r = await fetch(url, { method: isEdit ? 'PUT' : 'POST', headers, body: JSON.stringify(payload) });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
      toast.success(isEdit ? 'Updated' : 'Created');
      setEditing(null); setForm({}); await load(); refetchRefs();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.name || item.label || item.title || item.slug || item.id}"?`)) return;
    try {
      const r = await fetch(`${API}/api/ib/admin/${resource}/${item.id}`, { method: 'DELETE', headers });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      toast.success('Deleted'); await load(); refetchRefs();
    } catch (e) { toast.error(`Delete failed: ${e.message}`); }
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(i => JSON.stringify(i).toLowerCase().includes(q));
  }, [items, search]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder={`Search ${schema.label.toLowerCase()}…`} className="pl-9" data-testid={`ibm-${resource}-search`} />
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid={`ibm-${resource}-refresh`}><RefreshCw className="w-4 h-4 mr-1" />Refresh</Button>
        <Button size="sm" onClick={() => { setEditing({}); setForm(emptyForm(schema)); }} className="bg-ux-cyan hover:bg-ux-cyan/90" data-testid={`ibm-${resource}-create`}>
          <Plus className="w-4 h-4 mr-1" />New {schema.singular}
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-ux-cyan" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-gray-500 text-sm">No {schema.label.toLowerCase()} yet.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {schema.cols.map(c => <TableHead key={c}>{c}</TableHead>)}
                  <TableHead>Status</TableHead>
                  <TableHead className="w-28">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(item => (
                  <TableRow key={item.id}>
                    {schema.cols.map(c => (
                      <TableCell key={c} className="font-mono text-xs">
                        {Array.isArray(item[c]) ? item[c].length + ' items' : String(item[c] ?? '—')}
                      </TableCell>
                    ))}
                    <TableCell>
                      {item.is_active === false
                        ? <Badge variant="outline" className="text-gray-500">inactive</Badge>
                        : <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">live</Badge>}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button size="icon" variant="ghost" onClick={() => { setEditing(item); setForm(toForm(schema, item)); }}><Pencil className="w-4 h-4" /></Button>
                        <Button size="icon" variant="ghost" onClick={() => remove(item)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && (setEditing(null), setForm({}))}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? `Edit ${schema.singular}` : `New ${schema.singular}`}</DialogTitle>
            <DialogDescription>Fields with <span className="text-red-500">*</span> are required.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {schema.fields.map(f => {
              // Full-width prominent card for the is_active toggle so it's
              // consistent with all other CMS admins site-wide
              if (f.key === 'is_active') {
                return (
                  <div key={f.key} className="md:col-span-2">
                    <PublishStatusCard
                      isPublished={!!form[f.key]}
                      onChange={(v) => setForm(prev => ({ ...prev, [f.key]: v }))}
                      entityLabel={schema.singular.toLowerCase()}
                      publicRoute="/ib"
                      testId={`ib-${resource}-publish-toggle`}
                    />
                  </div>
                );
              }
              return (
                <div key={f.key} className={['textarea', 'lines', 'json'].includes(f.type) ? 'md:col-span-2' : ''}>
                  <Label className="text-xs flex items-center gap-1">{f.label}{f.required && <span className="text-red-500">*</span>}</Label>
                  <div className="mt-1">
                    <FieldInput field={f} value={form[f.key]} onChange={(v) => setForm(prev => ({ ...prev, [f.key]: v }))} refs={refs} />
                  </div>
                  {f.hint && f.type !== 'json' && <p className="text-[10px] text-gray-400 mt-1">{f.hint}</p>}
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { setEditing(null); setForm({}); }}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-ux-cyan hover:bg-ux-cyan/90">
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              {editing?.id ? 'Save changes' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const IBPathwayManager = () => {
  const { token } = useCMSAuth();
  const [tab, setTab] = useState('faculties');
  const [refs, setRefs] = useState({ faculties: [], streams: [] });

  const headers = useMemo(() => ({ 'Authorization': `Bearer ${token}` }), [token]);

  const refetchRefs = useCallback(async () => {
    try {
      const [f, s] = await Promise.all([
        fetch(`${API}/api/ib/admin/faculties`, { headers }).then(r => r.ok ? r.json() : []),
        fetch(`${API}/api/ib/admin/streams`, { headers }).then(r => r.ok ? r.json() : []),
      ]);
      setRefs({ faculties: f, streams: s });
    } catch {/* ignore */}
  }, [headers]);

  useEffect(() => { if (token) refetchRefs(); }, [token, refetchRefs]);

  return (
    <AdminLayout>
      <div className="p-6 space-y-6" data-testid="ib-pathway-manager">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-ux-cyan/10 flex items-center justify-center">
              <GraduationCap className="w-5 h-5 text-ux-cyan" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">IB Pathway Explorer</h1>
              <p className="text-sm text-gray-500">Manage faculties, streams, and the 200+ university cards shown on the public explorer.</p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline" size="sm">
              <a href="/ib" target="_blank" rel="noreferrer"><ExternalLink className="w-4 h-4 mr-1" /> Open public explorer</a>
            </Button>
            <Button asChild variant="outline" size="sm">
              <a href={`${API}/api/ib/sitemap.xml`} target="_blank" rel="noreferrer"><Globe2 className="w-4 h-4 mr-1" /> View sitemap</a>
            </Button>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            {RESOURCES.map(r => (
              <TabsTrigger key={r} value={r} data-testid={`ibm-tab-${r}`}>{SCHEMAS[r].label}</TabsTrigger>
            ))}
          </TabsList>
          {RESOURCES.map(r => (
            <TabsContent key={r} value={r} className="mt-4">
              {token && <ResourceTab resource={r} token={token} refs={refs} refetchRefs={refetchRefs} />}
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default IBPathwayManager;
